// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { z } from 'zod';

/**
 * Recognized ISO 4217 codes. Mirrors `currency.AllCurrencies` on the BE —
 * keep the two registries in sync.
 */
export const isoCurrencyCodeSchema = z.enum([
  'USD',
  'EUR',
  'GBP',
  'JPY',
  'CHF',
  'CAD',
  'AUD',
  'NZD',
  'SEK',
  'NOK',
  'DKK',
  'CNY',
  'INR',
  'HKD',
  'SGD',
  'TWD',
  'KRW',
  'MYR',
  'IDR',
  'PHP',
  'THB',
  'VND',
  'MXN',
  'BRL',
  'ZAR',
]);

export type IsoCurrencyCode = z.infer<typeof isoCurrencyCodeSchema>;

interface IsoCurrencyInfo {
  symbol: string;
  long: string;
}

/** Display data per recognized ISO code. Mirrors `currency.AllCurrencies` on the BE. */
export const ALL_CURRENCIES: Record<IsoCurrencyCode, IsoCurrencyInfo> = {
  USD: { symbol: '$', long: 'US dollar' },
  EUR: { symbol: '€', long: 'euro' },
  GBP: { symbol: '£', long: 'British pound' },
  JPY: { symbol: '¥', long: 'Japanese yen' },
  CHF: { symbol: 'CHF', long: 'Swiss franc' },
  CAD: { symbol: 'CA$', long: 'Canadian dollar' },
  AUD: { symbol: 'A$', long: 'Australian dollar' },
  NZD: { symbol: 'NZ$', long: 'New Zealand dollar' },
  SEK: { symbol: 'kr', long: 'Swedish krona' },
  NOK: { symbol: 'kr', long: 'Norwegian krone' },
  DKK: { symbol: 'kr', long: 'Danish krone' },
  CNY: { symbol: '¥', long: 'Chinese yuan' },
  INR: { symbol: '₹', long: 'Indian rupee' },
  HKD: { symbol: 'HK$', long: 'Hong Kong dollar' },
  SGD: { symbol: 'S$', long: 'Singapore dollar' },
  TWD: { symbol: 'NT$', long: 'New Taiwan dollar' },
  KRW: { symbol: '₩', long: 'South Korean won' },
  MYR: { symbol: 'RM', long: 'Malaysian ringgit' },
  IDR: { symbol: 'Rp', long: 'Indonesian rupiah' },
  PHP: { symbol: '₱', long: 'Philippine peso' },
  THB: { symbol: '฿', long: 'Thai baht' },
  VND: { symbol: '₫', long: 'Vietnamese dong' },
  MXN: { symbol: 'MX$', long: 'Mexican peso' },
  BRL: { symbol: 'R$', long: 'Brazilian real' },
  ZAR: { symbol: 'R', long: 'South African rand' },
};

/**
 * Recognized ISO 4217 codes whose minor unit is zero (whole-unit currencies).
 * Mirrors `currency.ZeroDecimalCurrencies` on the BE — keep in sync.
 */
export const ZERO_DECIMAL_CURRENCIES: ReadonlySet<IsoCurrencyCode> = new Set([
  'JPY',
  'KRW',
  'VND',
]);

/**
 * Curated subset offered by app currency dropdowns (the org default
 * selector and `FormCurrencyTextField`), distinct from the full recognized
 * registry the BE supports. The org default schema derives from this set so
 * an organization's default is always offerable.
 */
export const APP_ALLOWED_CURRENCIES = [
  'USD',
  'CAD',
  'GBP',
  'EUR',
] as const satisfies readonly IsoCurrencyCode[];
